import { api } from "@breakout/backend/convex/_generated/api"
import { BASE_CHAIN_ID, TRADING } from "@breakout/shared"
import { useToast } from "@breakout/ui/components/Toast"
import { formatUsd, shortenAddress } from "@breakout/ui/utils"
import { convexQuery } from "@convex-dev/react-query"
import { useFundWallet, usePrivy, useWallets } from "@privy-io/react-auth"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useRouter } from "@tanstack/react-router"
import { zodSearchValidator } from "@tanstack/router-zod-adapter"
import { ArrowDownToLine, ArrowUpRight, Check, Loader2, Plus } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { z } from "zod"
import { PageContainer } from "../../components/layout/PageContainer"
import { WithdrawModal } from "../../components/modals/WithdrawModal"
import { PositionsSection } from "../../components/profile/PositionsSection"
import { ProfileActivityFeed } from "../../components/profile/ProfileActivityFeed"
import { ProfileHeader, ProfileStat } from "../../components/profile/ProfileHeader"
import { ProfilePageSkeleton } from "../../components/profile/ProfileSkeletons"
import { DEFAULT_POSITION_STATS, formatPnl } from "../../components/profile/pnlUtils"
import { useDocumentTitle } from "../../hooks/common/useDocumentTitle"
import { useTabNavigation } from "../../hooks/common/useTabNavigation"
import { useUserPositionsEnhanced } from "../../hooks/indexer/useUserPositionsEnhanced"
import { useClaimAll } from "../../hooks/trading/useClaimAll"
import { useAuthState } from "../../hooks/user/useAuthState"
import { useUsdcBalance } from "../../hooks/wallet/useUsdcBalance"
import { breakoutChain, useBreakoutConfig } from "../../lib/providers"
import { findEmbeddedWallet } from "../../lib/wallet"

const profileSearchSchema = z.object({
	tab: z.enum(["positions", "activity"]).optional().catch(undefined),
	status: z.enum(["active", "closed"]).optional().catch(undefined),
})

type TabType = "positions" | "activity"

export const Route = createFileRoute("/profile/")({
	component: ProfilePage,
	validateSearch: zodSearchValidator(profileSearchSchema),
})

function ProfilePage() {
	// 1. Routing params
	const router = useRouter()
	const { tab, status } = Route.useSearch()

	// 2. Context/store access
	const { navigateWithToast } = useTabNavigation()
	const { showToast } = useToast()
	const { ready, authenticated } = usePrivy()
	const { wallets } = useWallets()
	const { fundWallet } = useFundWallet()
	const { evmWallet } = useAuthState()
	const { claimAll, isClaiming, progress } = useClaimAll()
	const { chainId } = useBreakoutConfig()

	// Local state
	const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false)

	const activeTab: TabType = tab ?? "positions"
	const statusFilter: "active" | "closed" = status ?? "active"
	const walletAddress =
		evmWallet?.address && isAddress(evmWallet.address) ? getAddress(evmWallet.address) : null

	// Get embedded wallet for withdraw functionality
	const embeddedWallet = findEmbeddedWallet(wallets)

	// 3. Data queries
	const { data: convexUser } = useQuery({
		...convexQuery(api.domains.user.auth.query.current, {}),
		enabled: authenticated,
	})

	const {
		data: positionsData,
		isLoading: isLoadingPositions,
		error: positionsError,
	} = useUserPositionsEnhanced(walletAddress ? { userAddress: walletAddress } : null)

	const { data: balanceData, isLoading: isLoadingBalance } = useUsdcBalance(
		walletAddress ? { walletAddress } : null,
	)

	// 4. Derived values
	// Show skeleton until auth is ready AND we have wallet address
	// Positions data loading is handled by PositionsSection component
	const isPageLoading = !ready || (!walletAddress && authenticated)

	// Get claimable positions for Claim All (only from finalized winners)
	const claimablePositions = useMemo(() => {
		if (!positionsData?.positions) {
			return []
		}
		return positionsData.positions
			.filter(p => {
				const isWinner = p.isResolved && p.winningBucket === p.bucketIndex
				return p.isFinalized && isWinner && !p.claimed
			})
			.filter(p => isAddress(p.marketAddress))
			.map(p => ({
				marketAddress: getAddress(p.marketAddress),
				claimableAmount: p.shares,
			}))
	}, [positionsData?.positions])

	// Stats from backend
	const stats = positionsData?.stats ?? DEFAULT_POSITION_STATS

	const balance = balanceData ? Number(balanceData.balance) / 1e6 : 0

	// Update document title
	useDocumentTitle("Profile")

	// 5. Event handlers
	const setActiveTab = (newTab: TabType) => {
		navigateWithToast({
			to: "/profile",
			search: { tab: newTab === "positions" ? undefined : newTab, status },
			replace: true,
			resetScroll: false,
		})
	}

	const setStatusFilter = (newStatus: "active" | "closed") => {
		navigateWithToast({
			to: "/profile",
			search: { tab, status: newStatus === "active" ? undefined : newStatus },
			replace: true,
			resetScroll: false,
		})
	}

	const handleClaimAll = () => {
		if (claimablePositions.length > 0) {
			void claimAll(claimablePositions)
		}
	}

	// Get embedded wallet address for funding
	const embeddedWalletAddress = embeddedWallet?.address

	const handleAddCash = useCallback(async () => {
		if (!embeddedWalletAddress) {
			return
		}

		// Privy onramps only work on mainnet
		if (chainId !== BASE_CHAIN_ID) {
			showToast("Funding only works on mainnet", "info")
			return
		}

		try {
			await fundWallet({
				address: embeddedWalletAddress,
				options: { chain: breakoutChain },
			})
		} catch (error) {
			console.warn("[FundWallet] Error opening fund wallet:", error)
		}
	}, [embeddedWalletAddress, fundWallet, chainId, showToast])

	// Show skeleton while auth initializes
	if (isPageLoading) {
		return <ProfilePageSkeleton activeTab={activeTab} statusFilter={statusFilter} />
	}

	if (!authenticated) {
		router.navigate({ to: "/" })
		return null
	}

	const displayName = convexUser?.username
		? `@${convexUser.username}`
		: shortenAddress(evmWallet?.address || "")

	const hasClaimable = stats.totalClaimable > 0
	const isClaimBelowMinimum =
		stats.totalClaimable > 0 && stats.totalClaimable < TRADING.MIN_BUY_AMOUNT

	return (
		<>
			<ProfileHeader
				activeTab={activeTab}
				avatarUrl={convexUser?.avatarUrl ?? null}
				displayName={displayName}
				onTabChange={setActiveTab}
				stats={
					<>
						{/* Cash Balance Card */}
						<ProfileStat
							actions={
								embeddedWallet && walletAddress ? (
									<div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
										{/* Add Cash - Primary action */}
										<button
											className="inline-flex items-center justify-center gap-1 rounded-lg bg-breakout-brand px-2.5 py-1.5 font-semibold text-black text-xs shadow-glow transition-all hover:bg-breakout-brand-hover hover:shadow-glow-lg active:scale-[0.98] sm:gap-1.5 sm:px-3 sm:py-2 sm:text-sm"
											onClick={handleAddCash}
											type="button"
										>
											<Plus aria-hidden="true" className="size-3 sm:size-3.5" />
											<span>Add</span>
										</button>
										{/* Withdraw - Secondary action */}
										{balance > 0 && !isLoadingBalance && (
											<button
												className="inline-flex items-center justify-center gap-1 rounded-lg border border-border-subtle bg-bg-card/50 px-2 py-1.5 font-medium text-text-secondary text-xs transition-all hover:border-border-highlight hover:text-text-primary active:scale-[0.98] sm:gap-1.5 sm:px-2.5 sm:py-2 sm:text-sm dark:border-white/15 dark:text-white/70 dark:hover:text-white"
												onClick={() => setIsWithdrawModalOpen(true)}
												type="button"
											>
												<ArrowDownToLine aria-hidden="true" className="size-3 sm:size-3.5" />
												<span className="hidden sm:inline">Withdraw</span>
											</button>
										)}
									</div>
								) : undefined
							}
							icon={<span className="font-semibold text-text-muted text-xs sm:text-sm">$</span>}
							label="Cash"
							value={isLoadingBalance ? "—" : formatUsd(balance)}
						/>

						{/* Portfolio Value Card */}
						<ProfileStat
							actions={
								!isLoadingPositions ? (
									<span
										className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold text-xs tabular-nums sm:px-2 sm:py-1 ${
											stats.totalPnl > 0
												? "bg-emerald-500/10 text-emerald-600"
												: stats.totalPnl < 0
													? "bg-rose-500/10 text-rose-600"
													: "bg-text-muted/10 text-text-muted"
										}`}
									>
										{formatPnl(stats.totalPnl)}
									</span>
								) : undefined
							}
							icon={
								<ArrowUpRight aria-hidden="true" className="size-3 text-blue-500 sm:size-3.5" />
							}
							iconBgClass="bg-blue-500/10"
							label="Portfolio"
							value={isLoadingPositions ? "—" : formatUsd(stats.totalValue)}
						/>

						{/* Claimable Card */}
						<ProfileStat
							actions={
								hasClaimable && !isLoadingPositions ? (
									<button
										className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-breakout-brand px-3 py-2 font-semibold text-black text-xs shadow-glow transition-all hover:bg-breakout-brand-hover hover:shadow-glow-lg active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 sm:py-2.5 sm:text-sm"
										disabled={isClaiming || isClaimBelowMinimum}
										onClick={handleClaimAll}
										type="button"
									>
										{isClaiming ? (
											<>
												<Loader2 aria-hidden="true" className="size-3 animate-spin sm:size-4" />
												<span className="hidden sm:inline">{progress.step}</span>
												<span className="sm:hidden">
													{progress.current}/{progress.total}
												</span>
											</>
										) : isClaimBelowMinimum ? (
											<span>Minimum $1</span>
										) : (
											<span>Claim All</span>
										)}
									</button>
								) : undefined
							}
							highlight={hasClaimable}
							icon={
								<Check
									aria-hidden="true"
									className={`size-3 sm:size-3.5 ${hasClaimable ? "text-breakout-brand" : "text-text-muted"}`}
								/>
							}
							iconBgClass={hasClaimable ? "bg-breakout-brand/20" : "bg-text-muted/10"}
							label="Claimable"
							value={isLoadingPositions ? "—" : formatUsd(stats.totalClaimable)}
							valueClassName={hasClaimable ? "text-breakout-brand" : undefined}
						/>
					</>
				}
				variant="owner"
			/>

			{/* Content */}
			<PageContainer className="pt-4 pb-8 sm:pt-6">
				<div aria-labelledby={`tab-${activeTab}`} id={`tabpanel-${activeTab}`} role="tabpanel">
					{activeTab === "positions" ? (
						<PositionsSection
							error={positionsError}
							isLoading={isLoadingPositions}
							onStatusFilterChange={setStatusFilter}
							positionsData={positionsData}
							statusFilter={statusFilter}
							variant="owner"
							walletAddress={walletAddress}
						/>
					) : (
						<ActivityTab userAddress={walletAddress} />
					)}
				</div>
			</PageContainer>

			{/* Withdraw Modal */}
			{balanceData && (
				<WithdrawModal
					balance={balanceData.balance}
					isOpen={isWithdrawModalOpen}
					onClose={() => setIsWithdrawModalOpen(false)}
				/>
			)}
		</>
	)
}

function ActivityTab({ userAddress }: { userAddress: Address | null }) {
	return <ProfileActivityFeed userAddress={userAddress} />
}
